import apiClient from "./api/client";
import ENDPOINTS from "./api/endpoints";

/**
 * Upload Service - handles file upload operations
 * Used for uploading branch logos and other assets
 */
const uploadService = {
  /**
   * Upload a branch logo image
   * @param {File} file - The image file to upload
   * @returns {Promise<Object>} Upload result with logoUrl
   */
  uploadLogo: async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.post(ENDPOINTS.UPLOADS.LOGO, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response;
    } catch (error) {
      console.error("Failed to upload logo:", error);
      throw error;
    }
  },

  /**
   * Delete a branch logo
   * @param {string} relativePath - The relative path of the logo to delete
   * @returns {Promise<Object>} Delete result
   */
  deleteLogo: async (relativePath) => {
    try {
      const response = await apiClient.delete(ENDPOINTS.UPLOADS.DELETE_LOGO, {
        params: { path: relativePath }
      });
      return response;
    } catch (error) {
      console.error("Failed to delete logo:", error);
      throw error;
    }
  },

  /**
   * Get upload configuration (allowed extensions, max file size)
   * @returns {Promise<Object>} Upload configuration
   */
  getUploadConfig: async () => {
    try {
      const response = await apiClient.get(ENDPOINTS.UPLOADS.CONFIG);
      return response;
    } catch (error) {
      console.error("Failed to get upload config:", error);
      throw error;
    }
  },

  /**
   * Validate file before upload (client-side validation)
   * @param {File} file - The file to validate
   * @param {Object} config - Upload configuration
   * @returns {Object} Validation result { valid: boolean, error: string }
   */
  validateFile: (file, config = {}) => {
    const allowedExtensions = config.allowedExtensions || ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp', 'svg', 'tiff', 'tif', 'ico', 'heic', 'heif', 'avif'];
    const maxFileSize = config.maxFileSize || 5242880; // 5MB default

    if (!file) {
      return { valid: false, error: 'No file selected' };
    }

    // Check file type
    const extension = file.name.split('.').pop().toLowerCase();
    if (!allowedExtensions.includes(extension)) {
      return {
        valid: false,
        error: `Invalid file type. Allowed types: ${allowedExtensions.join(', ')}`
      };
    }

    // Check file size
    if (file.size > maxFileSize) {
      return {
        valid: false,
        error: `File size exceeds ${maxFileSize >= 1048576 ? Math.round(maxFileSize / 1048576) + 'MB' : Math.round(maxFileSize / 1024) + 'KB'} limit`
      };
    }

    // Check if it's an image
    if (!file.type.startsWith('image/')) {
      return { valid: false, error: 'Only image files are allowed' };
    }

    return { valid: true, error: null };
  },

  /**
   * Get the full URL for a logo path
   * @param {string} logoUrl - The logo URL from the database
   * @returns {string} Full URL to access the logo
   */
  getLogoUrl: (logoUrl) => {
    if (!logoUrl) return null;

    // If it's already a full URL, return as is
    if (logoUrl.startsWith('http://') || logoUrl.startsWith('https://')) {
      return logoUrl;
    }

    // If it's a relative path starting with /api/uploads, prepend the API base URL
    if (logoUrl.startsWith('/api/uploads')) {
      const baseUrl = apiClient.defaults?.baseURL || '';
      return baseUrl + logoUrl;
    }

    return logoUrl;
  },
};

export default uploadService;
